import React, { useRef } from "react";
import { X } from "lucide-react";
import { StickerBody, stickerWrapStyle, visibleStickers, LINK_STICKER_SCALE, SAFE_TOP_FR, SAFE_BOTTOM_FR } from "@/components/StoryMedia";
import { clamp, dist, angleDeg, centroid, snapAngle } from "./storyEditorUtils";

// Stickers déplaçables : 1 doigt = glisser, 2 doigts = pincer (taille) + tourner, relâcher sur la
// corbeille = supprimer, tap simple sur une bulle de texte = la modifier.
// `embed` : carte de lien en cours → l'URL disparaît des bulles (comme dans la visionneuse) ; retirer la carte la fait réapparaître.
// Lot 664 : `safeRefs` = {top, bottom} (repères de la zone sûre de la visionneuse : en-tête nom/photo, barre « Répondre ») →
// le centre d'un sticker ne peut pas être glissé derrière.
export const StoryStickerLayer = ({ stickers, setStickers, selected, setSelected, frameRef, trashRef, onEditText, onRemove, onDragState, disabled, embed = null, safeRefs = null, t }) => {
  const g = useRef(null);
  const shown = visibleStickers(stickers, embed);
  const yBounds = (frameHeight) => {
    const top = safeRefs?.top?.current?.offsetHeight || 0;
    const bottom = safeRefs?.bottom?.current?.offsetHeight || 0;
    if (!frameHeight || (!top && !bottom)) return [0.02, 0.98];
    return [Math.max(0.02, top / frameHeight + 0.03), Math.min(0.98, 1 - bottom / frameHeight - 0.03)];
  };

  const snapshot = (pts, s) => {
    const arr = [...pts.values()];
    return { x: s.x, y: s.y, scale: s.scale, rotation: s.rotation || 0, c: centroid(arr), d: arr.length >= 2 ? dist(arr[0], arr[1]) : 0, a: arr.length >= 2 ? angleDeg(arr[0], arr[1]) : 0 };
  };
  const overTrash = (p) => {
    const r = trashRef.current?.getBoundingClientRect();
    return !!r && p.x >= r.left - 12 && p.x <= r.right + 12 && p.y >= r.top - 12 && p.y <= r.bottom + 12;
  };

  const down = (e, s) => {
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    setSelected(s.id);
    const cur = g.current && g.current.id === s.id ? g.current : { id: s.id, type: s.type, long: !!s.long, pts: new Map(), moved: false };
    cur.pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
    cur.start = snapshot(cur.pts, s);
    g.current = cur;
    onDragState({ dragging: true, overTrash: false });
  };
  const move = (e) => {
    const cur = g.current;
    if (!cur || !cur.pts.has(e.pointerId)) return;
    if (cur.long) return; // lot 713 — un « texte long » reste centré et ajusté au cadre : ni glisser, ni pincer (tap = modifier)
    cur.pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const r = frameRef.current.getBoundingClientRect();
    const arr = [...cur.pts.values()];
    const c = centroid(arr);
    const dx = (c.x - cur.start.c.x) / r.width, dy = (c.y - cur.start.c.y) / r.height;
    if (Math.abs(dx) + Math.abs(dy) > 0.006) cur.moved = true;
    const [yMin, yMax] = yBounds(r.height);
    const upd = { x: clamp(cur.start.x + dx, 0.02, 0.98), y: clamp(cur.start.y + dy, yMin, yMax) };
    if (arr.length >= 2 && cur.start.d > 0) {
      const lim = cur.type === "link" ? LINK_STICKER_SCALE : { min: 0.02, max: 0.6 };
      upd.scale = clamp(cur.start.scale * (dist(arr[0], arr[1]) / cur.start.d), lim.min, lim.max);
      upd.rotation = snapAngle(cur.start.rotation + (angleDeg(arr[0], arr[1]) - cur.start.a));
      cur.moved = true;
    }
    setStickers((list) => list.map((s) => (s.id === cur.id ? { ...s, ...upd } : s)));
    onDragState({ dragging: true, overTrash: arr.length === 1 && overTrash(c) });
  };
  const up = (e) => {
    const cur = g.current;
    if (!cur || !cur.pts.has(e.pointerId)) return;
    cur.pts.delete(e.pointerId);
    const s = stickers.find((x) => x.id === cur.id);
    if (cur.pts.size > 0) { if (s) cur.start = snapshot(cur.pts, s); return; }
    g.current = null;
    onDragState({ dragging: false, overTrash: false });
    if (cur.moved && overTrash({ x: e.clientX, y: e.clientY })) { remove(cur.id); return; }
    if (!cur.moved && s?.type === "text") onEditText(s);
  };
  const remove = (id) => {
    const s = stickers.find((x) => x.id === id);
    setStickers((list) => list.filter((x) => x.id !== id));
    setSelected((cur) => (cur === id ? null : cur));
    if (s) onRemove?.(s);
  };

  return (
    <div className="absolute inset-0 z-10 pointer-events-none" data-testid="story-fs-sticker-layer" data-count={stickers.length} data-shown={shown.length}
      data-story-frame="1" data-safe-top={SAFE_TOP_FR} data-safe-bottom={SAFE_BOTTOM_FR}>
      {shown.map((s) => (
        <div
          key={s.id}
          data-sticker="1"
          className={`absolute touch-none select-none ${disabled ? "pointer-events-none" : "pointer-events-auto"} ${selected === s.id ? "outline outline-2 outline-white/80 outline-offset-2 rounded" : ""}`}
          style={stickerWrapStyle(s)}
          onPointerDown={(e) => down(e, s)}
          onPointerMove={move}
          onPointerUp={up}
          onPointerCancel={up}
          data-testid={`story-sticker-${s.id}`}
          data-type={s.type}
        >
          <StickerBody s={s} embed={embed} />
          {selected === s.id && (
            <button
              type="button"
              aria-label={t("common.delete", "Supprimer")}
              onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
              onClick={(e) => { e.stopPropagation(); remove(s.id); }}
              className="absolute grid place-items-center rounded-full bg-black/80 text-white shadow"
              style={{ top: "-12px", right: "-12px", width: "24px", height: "24px", touchAction: "manipulation" }}
              data-testid={`story-sticker-remove-${s.id}`}
            >
              <X size={14} />
            </button>
          )}
        </div>
      ))}
    </div>
  );
};
